// Production board: kanban over the channel-aware stages, in-flight work
// only (ideas live in the Idea bank, published pieces in Performance).
// Moving a card (drag between columns, or the quick advance button)
// updates status and opens the time-on-move prompt (handoff 7.5).

import { useMemo, useState } from 'react';
import { useContent } from '../../../context/ContentContext';
import { boardStagesForChannel, STAGE_LABELS, nextStage, CHANNELS } from '../stages';
import { getPillarLabel } from '../pillars';
import { parseLocalDate } from '../dates';
import TimePromptModal from './TimePromptModal';
import { Pill } from '../ui';

const CHANNEL_EDGE = { blog: 'border-l-bullogic-blue', youtube: 'border-l-rose' };

export default function BoardView() {
  const { entries, saveOne } = useContent();
  const [channel, setChannel] = useState('blog');
  const [dragId, setDragId] = useState(null);
  const [dropStage, setDropStage] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [err, setErr] = useState(null);

  const stages = boardStagesForChannel(channel);
  const byStage = useMemo(() => {
    const map = Object.fromEntries(stages.map(s => [s, []]));
    for (const e of entries) {
      if ((e.channel || 'blog') !== channel) continue;
      if (map[e.status]) map[e.status].push(e);
    }
    return map;
  }, [entries, channel, stages]);

  async function moveTo(entry, toStage) {
    if (!entry || entry.status === toStage) return;
    const fromStage = entry.status;
    setErr(null);
    try {
      const updates = { ...entry, status: toStage };
      if (toStage === 'published' && !entry.publishDate) {
        updates.publishDate = new Date().toISOString().slice(0, 10);
      }
      await saveOne(updates);
      setPrompt({ contentId: entry.id, title: entry.title, fromStage, toStage });
    } catch (e) {
      setErr(e.message);
    }
  }

  function handleDrop(stage) {
    const entry = entries.find(e => e.id === dragId);
    setDragId(null);
    setDropStage(null);
    if (entry) moveTo(entry, stage);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1 bg-white border border-line rounded-[9px] p-[3px] w-max">
          {CHANNELS.map(c => (
            <button
              key={c.id}
              onClick={() => setChannel(c.id)}
              className={`text-[12.5px] font-bold px-3.5 py-1 rounded-[6px] transition-colors ${
                channel === c.id ? 'bg-deep-teal text-white' : 'text-ink-2 hover:text-navy'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <span className="text-ink-3 text-[12px]">Drag a card (or use →) to advance it; each move asks for time.</span>
      </div>

      {err && <div className="text-[12px] text-rose mb-2">Move failed: {err}</div>}

      <div className="grid gap-3 items-start" style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}>
        {stages.map(stage => (
          <div
            key={stage}
            onDragOver={e => { e.preventDefault(); setDropStage(stage); }}
            onDragLeave={() => setDropStage(s => (s === stage ? null : s))}
            onDrop={() => handleDrop(stage)}
          >
            <div className="flex items-center justify-between px-1 pb-2">
              <span className="text-[10.5px] font-black uppercase tracking-[0.04em] text-ink-2">{STAGE_LABELS[stage]}</span>
              <span className="text-[10.5px] font-black text-ink-3 bg-line-2 rounded-[5px] px-1.5">{byStage[stage].length}</span>
            </div>
            <div className={`flex flex-col gap-2 min-h-[48px] rounded-[6px] ${
              dropStage === stage ? 'outline-2 outline-dashed outline-bullogic-blue/50 outline-offset-3' : ''
            }`}>
              {byStage[stage].map(e => (
                <div
                  key={e.id}
                  draggable
                  onDragStart={() => setDragId(e.id)}
                  className={`bg-white border border-line border-l-[3px] ${CHANNEL_EDGE[channel]} rounded-[6px] shadow-[0_1px_3px_rgba(2,10,40,0.06)] px-2.5 py-2 cursor-grab hover:shadow-[0_8px_24px_rgba(2,10,40,0.10)]`}
                >
                  <div className="text-[12px] font-bold text-navy leading-snug">{e.title}</div>
                  <div className="flex items-center justify-between mt-1.5 gap-1.5">
                    <span className="text-[10.5px] font-bold text-ink-2 bg-line-2 px-1.5 py-px rounded-[5px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[70%]">
                      {e.planWeek
                        ? parseLocalDate(e.planWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : getPillarLabel(e.pillar).split(' ')[0]}
                    </span>
                    {nextStage(stage, channel) && (
                      <button
                        onClick={() => moveTo(e, nextStage(stage, channel))}
                        title={`Advance to ${STAGE_LABELS[nextStage(stage, channel)]}`}
                        className="text-bullogic-blue text-[13px] font-black hover:text-navy px-1"
                      >
                        →
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {byStage[stage].length === 0 && (
                <div className="text-[11px] text-ink-3 italic px-1 py-2">empty</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {stages.every(s => byStage[s].length === 0) && (
        <div className="mt-4 text-[12.5px] text-ink-2">
          Nothing in flight for {channel === 'blog' ? 'the blog' : 'YouTube'}.
          Promote an idea from the <Pill tone="neutral">Idea bank</Pill> to get a piece moving.
        </div>
      )}

      <TimePromptModal prompt={prompt} onClose={() => setPrompt(null)} />
    </div>
  );
}
